function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// ----------------------------------------------------------------------------
// Product Form Web Component: iamProductPrice <iam-product-price>
// Extends: iamFormElement
// ----------------------------------------------------------------------------
// Displays the product price in the product form.
// - It updates the rendered price markup locally whenever a variant is changed.
// - It prefers the theme-owned price container and ignores app-injected duplicates.
// ----------------------------------------------------------------------------
// Event Listeners:
// - 'productForm:variant:changed' → Listens for when the product variant is
//                                   changed to trigger updating the price
// ----------------------------------------------------------------------------
// Event Triggers:
// - 'productForm:price:updated'   → Triggered after the price is updated
// ----------------------------------------------------------------------------
// Key Data Mutations:
// - Normalizes the pricing row classes and updates compare/current price text in place.
// - Removes Avios app duplicate price blocks inside the component so only the theme-owned
//   price block remains visible and readable.
// ----------------------------------------------------------------------------

// Helpers
import { formatMoney } from '@shopify/theme-currency';
import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import iamFormElement from 'scripts/classes/iamFormElement';

// -------------------------------------
// Web Component
// -------------------------------------

class iamProductPrice extends iamFormElement {
  constructor() {
    super();
    this.priceMutationObserver = null;
    this.normalizeFrame = null;
    this.isNormalizingMarkup = false;
    this.scheduleMarkupNormalization = this.scheduleMarkupNormalization.bind(this);
  }

  /**
   * Initialize the component when the product form is initialized
   */
  onFormInit() {
    // Bind Event Listeners
    iamotaEventHandler.subscribe('productForm:variant:changed', this.onVariantChange.bind(this), {
      context: this.iamForm,
      source: "product-price.js"
    });
    this.observePriceMarkup();

    // Update the price to the inital value
    this.updatePrice(this.iamForm.getCurrentVariant());
  }
  disconnectedCallback() {
    var _this$priceMutationOb;
    (_this$priceMutationOb = this.priceMutationObserver) === null || _this$priceMutationOb === void 0 || _this$priceMutationOb.disconnect();
    cancelAnimationFrame(this.normalizeFrame);
    super.disconnectedCallback();
  }

  /**
   * Trigger actions when the variant changes
   * @param {variantObject} variant
   */
  onVariantChange(_ref) {
    var {
      variant
    } = _ref;
    this.updatePrice(variant);
  }

  /**
   * Update the price based on the specified variant.
   * @param {variantObject} variant
   */
  updatePrice(variant) {
    var priceContainer = this.getPriceContainer();
    var storedSurcharge = Number.parseInt(priceContainer.dataset.currentSurcharge, 10);
    var surcharge = Number.isFinite(storedSurcharge) ? storedSurcharge : 0;
    this.isNormalizingMarkup = true;
    try {
      this.removeAppPriceDuplicates();
      var pricingRow = this.getOrCreatePricingRow(priceContainer);
      var valueWrapper = this.getOrCreateValueWrapper(pricingRow);
      var currentPriceElement = this.getOrCreateCurrentPrice(valueWrapper);
      currentPriceElement.classList.add('product-price__current', 'price-item', 'price-item--regular');
      if (!variant) {
        // Preserve server-rendered markup if the variant object is temporarily unavailable.
        return;
      }
      var currentPrice = variant.price + surcharge;
      var comparePrice = this.syncComparePrice(pricingRow, variant, surcharge);
      currentPriceElement.innerHTML = formatMoney(currentPrice, window.theme.settingCurrencyFormat || '{{amount}}');
      currentPriceElement.dataset.priceCents = currentPrice;
      currentPriceElement.classList.toggle('product-price__current--sale', comparePrice);
      currentPriceElement.classList.toggle('price-item--sale', comparePrice);
      currentPriceElement.style.color = comparePrice ? 'var(--sale-color)' : 'var(--text-color)';
      priceContainer.dataset.currentPrice = currentPrice;
      priceContainer.dataset.currentSurcharge = surcharge;
    } finally {
      this.isNormalizingMarkup = false;
    }
    iamotaEventHandler.trigger('productForm:price:updated', {
      variant
    }, {
      context: this.iamForm
    });
  }
  getOrCreatePricingRow(priceContainer) {
    var pricingRow = priceContainer.querySelector('.product-price__pricing, .product__price');
    if (pricingRow) {
      pricingRow.className = 'product-price__pricing';
      return pricingRow;
    }
    var newPricingRow = document.createElement('div');
    newPricingRow.className = 'product-price__pricing';
    priceContainer.prepend(newPricingRow);
    return newPricingRow;
  }
  getOrCreateValueWrapper(pricingRow) {
    var valueWrapper = pricingRow.querySelector('.product-price__value, .price__original, .price__regular');
    if (valueWrapper) {
      valueWrapper.className = 'product-price__value';
      return valueWrapper;
    }
    var newValueWrapper = document.createElement('span');
    newValueWrapper.className = 'product-price__value';
    var inlinePriceText = this.extractPriceText(pricingRow);
    pricingRow.append(newValueWrapper);
    if (inlinePriceText) {
      newValueWrapper.textContent = inlinePriceText;
    }
    return newValueWrapper;
  }
  getOrCreateCurrentPrice(valueWrapper) {
    var currentPrice = valueWrapper.querySelector('[data-product-price], .product-price__current, .price__current');
    if (currentPrice) return currentPrice;
    var newCurrentPrice = document.createElement('span');
    newCurrentPrice.dataset.productPrice = '';
    var inlinePriceText = this.extractPriceText(valueWrapper);
    if (inlinePriceText) {
      newCurrentPrice.textContent = inlinePriceText;
      valueWrapper.textContent = '';
    }
    valueWrapper.append(newCurrentPrice);
    return newCurrentPrice;
  }
  syncComparePrice(pricingRow, variant) {
    var surcharge = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
    var currentPrice = variant.price + surcharge;
    var currentComparePrice = variant.compare_at_price ? variant.compare_at_price + surcharge : null;
    var isOnSale = currentComparePrice > currentPrice;
    var existingComparePrice = pricingRow.querySelector('[data-compare-price], .product-price__compare, .price__sale');
    if (!isOnSale) {
      var _pricingRow$querySele;
      existingComparePrice === null || existingComparePrice === void 0 || existingComparePrice.remove();
      (_pricingRow$querySele = pricingRow.querySelector('.visually-hidden')) === null || _pricingRow$querySele === void 0 || _pricingRow$querySele.remove();
      return null;
    }
    var comparePrice = existingComparePrice || document.createElement('s');
    comparePrice.className = 'product-price__compare price-item price-item--regular';
    comparePrice.dataset.comparePrice = '';
    comparePrice.hidden = false;
    comparePrice.innerHTML = formatMoney(currentComparePrice, window.theme.settingCurrencyFormat || '{{amount}}');
    if (!comparePrice.parentElement) {
      pricingRow.prepend(comparePrice);
    }
    return comparePrice;
  }
  removeAppPriceDuplicates() {
    this.querySelectorAll('.apo-variant-price:not([data-product-pricing="theme"])').forEach(node => {
      node.setAttribute('hidden', '');
      node.setAttribute('aria-hidden', 'true');
    });
  }
  observePriceMarkup() {
    var _this$priceMutationOb2;
    (_this$priceMutationOb2 = this.priceMutationObserver) === null || _this$priceMutationOb2 === void 0 || _this$priceMutationOb2.disconnect();
    this.priceMutationObserver = new MutationObserver(mutations => {
      if (this.isNormalizingMarkup) return;
      var shouldNormalize = !this.hasNormalizedThemePrice() || mutations.some(mutation => {
        var _mutation$target, _target$closest, _target$matches;
        var target = mutation.target instanceof Element ? mutation.target : (_mutation$target = mutation.target) === null || _mutation$target === void 0 ? void 0 : _mutation$target.parentElement;
        if (target !== null && target !== void 0 && (_target$closest = target.closest) !== null && _target$closest !== void 0 && _target$closest.call(target, '.apo-variant-price:not([data-product-pricing="theme"])')) return true;
        if (target !== null && target !== void 0 && (_target$matches = target.matches) !== null && _target$matches !== void 0 && _target$matches.call(target, '.product__price')) return true;
        return Array.from(mutation.addedNodes || []).some(node => node instanceof Element && (node.matches('.apo-variant-price:not([data-product-pricing="theme"]), .product__price') || node.querySelector('.apo-variant-price:not([data-product-pricing="theme"]), .product__price')));
      });
      if (!shouldNormalize) return;
      this.scheduleMarkupNormalization();
    });
    this.priceMutationObserver.observe(this, {
      childList: true,
      subtree: true
    });
  }
  scheduleMarkupNormalization() {
    cancelAnimationFrame(this.normalizeFrame);
    this.normalizeFrame = requestAnimationFrame(() => {
      this.updatePrice(this.iamForm.getCurrentVariant());
    });
  }
  hasNormalizedThemePrice() {
    var priceContainer = this.querySelector(iamProductPrice.THEME_PRICE_CONTAINER_SELECTOR);
    return Boolean(priceContainer && priceContainer.querySelector('.product-price__pricing') && priceContainer.querySelector('[data-product-price]'));
  }
  extractPriceText(element) {
    var textNodes = Array.from(element.childNodes || []).filter(node => node.nodeType === Node.TEXT_NODE && /\d/.test(node.textContent || ''));
    var textContent = textNodes.map(node => node.textContent.trim()).join(' ').trim();
    textNodes.forEach(node => node.remove());
    return textContent;
  }
  getPriceContainer() {
    var priceContainer = this.querySelector(iamProductPrice.THEME_PRICE_CONTAINER_SELECTOR);
    if (!priceContainer) {
      this.innerHTML = iamProductPrice.PRICE_CONTAINER_HTML;
      priceContainer = this.querySelector(iamProductPrice.THEME_PRICE_CONTAINER_SELECTOR);
    }
    return priceContainer;
  }
}
_defineProperty(iamProductPrice, "THEME_PRICE_CONTAINER_SELECTOR", '[data-product-pricing="theme"], [data-product-pricing]:not(.apo-variant-price)');
_defineProperty(iamProductPrice, "PRICE_CONTAINER_HTML", "\n        <div id=\"ProductPrice\" class=\"product-pricing product-price\" data-product-pricing=\"theme\"></div>\n    ");
customElements.define('iam-product-price', iamProductPrice);